import dayjs from 'dayjs';
import { orderBy } from 'lodash-es';

export interface TickerItem {
  id: number;
  content: string;
  times: number;
  sendTime: number;
  endTime: number | null;
  weight: number;
  status: number | null;
  type: number;
}

export class Ticker {
  private messages: TickerItem[] = [];
  private playCount: Map<number, number> = new Map();
  private currentIndex: number = -1;
  private messageIndexes: number[] = [];

  constructor(messages: TickerItem[] = []) {
    messages.forEach(msg => this.add(msg));
  }

  add(message: TickerItem) {
    const existingIndex = this.messages.findIndex(msg => msg.id === message.id);
    if (existingIndex === -1) {
      this.messages.push(message);
      this.playCount.set(message.id, 0);
      this.updateMessageIndexes();
    }
  }

  private isPlayable(message: TickerItem): boolean {
    const now = Date.now();
    const currentPlayCount = this.playCount.get(message.id) || 0;

    // 检查播放次数限制
    if (message.times > 0 && currentPlayCount >= message.times) {
      return false;
    }

    // 检查是否达到开始时间
    if (now < message.sendTime) {
      return false;
    }

    // 检查是否在有效期内
    if (message.endTime !== null && now > message.endTime) {
      return false;
    }

    return true;
  }

  private updateMessageIndexes() {
    // 将消息分为固定消息和普通消息
    const fixedMessages = this.messages.filter(msg => msg.status === 1);
    const normalMessages = this.messages.filter(msg => msg.status !== 1);

    // 对固定消息和普通消息分别进行排序
    const sortedFixedMessages = orderBy(
      fixedMessages.map((msg, index) => ({
        index,
        weight: msg.weight,
        sendTime: msg.sendTime,
        type: msg.type
      })),
      ['weight', 'type', 'sendTime'],
      ['asc', 'asc', 'desc']
    );

    const sortedNormalMessages = orderBy(
      normalMessages.map((msg, index) => ({
        index,
        weight: msg.weight,
        sendTime: msg.sendTime,
        type: msg.type
      })),
      ['weight', 'type', 'sendTime'],
      ['asc', 'asc', 'desc']
    );

    // 合并排序后的消息索引，固定消息在前，普通消息在后
    this.messageIndexes = [
      ...sortedFixedMessages.map(item => item.index),
      ...sortedNormalMessages.map(item => item.index)
    ];
  }

  getNext(): TickerItem | null {
    if (this.messages.length === 0) {
      return null;
    }

    const startIndex = this.currentIndex;
    let checkedCount = 0;

    while (checkedCount < this.messages.length) {
      this.currentIndex = (this.currentIndex + 1) % this.messageIndexes.length;

      if (this.currentIndex === startIndex && checkedCount > 0) {
        return null;
      }

      const messageIndex = this.messageIndexes[this.currentIndex];
      const message = this.messages[messageIndex];

      if (this.isPlayable(message)) {
        const currentCount = this.playCount.get(message.id) || 0;
        this.playCount.set(message.id, currentCount + 1);
        return message;
      }

      checkedCount++;
    }

    return null;
  }

  clear() {
    this.messages = [];
    this.playCount.clear();
    this.currentIndex = -1;
    this.messageIndexes = [];
  }

  getMessageStatus(messageId: number) {
    const message = this.messages.find(msg => msg.id === messageId);
    if (!message) return null;

    return {
      playCount: this.playCount.get(messageId) || 0,
      isPlayable: this.isPlayable(message)
    };
  }

  getSize() {
    return this.messages.length;
  }
}
